import { mountReportingSurface } from '../core/reportingSurface.js';
import { loadingState, beginRefresh, settleLoadingValues, failLoadingStates } from '../core/loadingState.js';
import { takePerformancePeriod } from '../core/performanceNavigation.js';
/**
 * analytics-route.js — client-saas /analytics (Analytics · Overview)
 *
 * Replaces the generic SECTION_ROUTES placeholder that used to sit on this path. The
 * nav has advertised Analytics › Overview for a while and it landed on a stub, which is
 * the same dead-end the Templates tab used to be.
 *
 * The grain here is the MAGNET, deliberately, and that is the whole reason the page
 * exists. /home already answers "how are we doing" as one blended number, and a
 * customer running four magnets has that number average away the fact that one of them
 * does all the work. Reads GET /m5t/v5/dashboard/stats?group_by=magnet, which returns
 * one row per magnet install.
 *
 * Chart is hand-rolled inline SVG, matching ad-performance-route.js: the app builds a
 * single inlined bundle, so pulling in a chart library would cost every page. (echarts
 * is in package.json but is deliberately unused in this app.)
 *
 * Honest empty states throughout — a magnet with no traffic reads as "no traffic yet",
 * never as a zero that looks like failure.
 */

import AuthController from '../services/AuthController.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import { statCard } from '../core/statCard.js';
import { capList } from '../core/listExpand.js';
import { ensureChartStyles, dualAxisChartHTML, wireChartHover } from '../services/chart-kit.js';
import { setHandoff, HANDOFF, setLeadsHandoff, leadsHandoffScope, setMagnetResultsHandoff } from '../core/routeHandoff.js';
import { magnetHomePath } from '../core/magnetOverview.js';
let drillRange = {}, drillScope;
const analyticsLeads = context => setLeadsHandoff({ ...(drillScope || leadsHandoffScope()), ...context, returnTo: { hash: '#/analytics', label: 'Back to Magnet comparison' } });
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtNum = (n) => Number(n || 0).toLocaleString();
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

// A rate the server could not compute (nothing opened) comes back null, NOT zero. The
// distinction matters on this page more than anywhere: 0% reads as "this magnet is
// failing", where the truth is "nobody has opened it yet".
const fmtRate = (r) => (r == null ? '—' : `${r}%`);

const PERIODS = [{ d: 7, label: '7 Days' }, { d: 30, label: '30 Days' }, { d: 90, label: '90 Days' }];
let periodDays = 30;

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-analytics-styles';
  s.textContent = `
    .page-shell .anl-wrap { overflow-x: auto; }
    .page-shell .anl-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    .page-shell .anl-table th { text-align: right; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; }
    .page-shell .anl-table th:first-child { text-align: left; }
    .page-shell .anl-table td { padding: 10px 12px; text-align: right; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); font-variant-numeric: tabular-nums; white-space: nowrap; }
    .page-shell .anl-table td:first-child { text-align: left; white-space: normal; }
    .page-shell .anl-table tr:last-child td { border-bottom: 0; }
    .page-shell .anl-name { font-weight: 600; color: inherit; text-decoration: none; }
    .page-shell .anl-name:hover { text-decoration: underline; }
    .page-shell .anl-sub { display: block; font-size: 0.74rem; font-weight: 400; color: var(--color-text-subtle, #6C7C9A); margin-top: 2px; }
    .page-shell .anl-chip { display: inline-block; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; padding: 2px 6px; border-radius: 5px; margin-left: 6px; vertical-align: 2px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .anl-bar { display: block; }
    /* Off the --color-* scale and onto the chart palette, so this mark follows the theme
       with every other chart mark rather than only matching in light mode. */
    .page-shell .anl-bar__track { fill: var(--chart-grid, #EAF0F7); }
    .page-shell .anl-bar__fill { fill: var(--chart-1, #0072BA); }
    .page-shell .anl-note { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: 12px 2px 0; }
    .page-shell .anl-warn { font-size: 0.78rem; color: var(--color-text-muted, #4A5A78); background: var(--color-surface, #F7FAFD); border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; padding: 8px 12px; margin: 0 0 12px; }
    .page-shell .chart-period-btn { cursor: pointer; }
    .page-shell .chart-period-btn.active { background: var(--color-primary-600, #0072BA); color: #fff; }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;



/**
 * A row's share of the period's leads, as a bar. Scaled against the busiest magnet
 * rather than against the total, so the ranking stays readable when one magnet
 * dominates — against the total, a 90/5/5 split renders as one bar and two slivers.
 */
function shareBar(leads, max) {
  // Share of the busiest magnet, not of the total (see the note above).
  //
  // The fill used --color-primary-600 while every other chart mark in the app uses
  // --chart-1. Same blue in light mode, DIFFERENT in dark — the chart tokens are tuned
  // per theme against the dark background and the colour tokens are not — so this bar
  // drifted off-palette exactly where it was hardest to notice. Colours now come from the
  // shared .m5c-* classes, like every other mark.
  //
  // title + aria-label give it the hover readout it never had. A 72px inline bar in a
  // table row is the one place the full tooltip machinery would be worse than a native
  // one: there is no band to hover, and a floating panel over a dense table fights the
  // row it is describing.
  const pct = max > 0 ? Math.round((leads / max) * 100) : 0;
  // Deliberately no percentage in the readout. This page refuses to print a fake 0% for a
  // magnet nothing has opened (see the leads-per-open cell), and a "(0%)" here — a
  // different metric entirely, share of the busiest magnet — reads as exactly that number
  // and lands in the row's text.
  const readout = `${fmtNum(leads)} of ${fmtNum(max)} leads for the busiest magnet`;
  return `<svg class="anl-bar" width="72" height="8" viewBox="0 0 72 8" role="img"
      aria-label="${esc(readout)}">
      <title>${esc(readout)}</title>
      <rect class="anl-bar__track" x="0" y="1" width="72" height="6" rx="3"></rect>
      ${pct > 0 ? `<rect class="anl-bar__fill" x="0" y="1" width="${Math.max(2, Math.round(pct * 0.72))}" height="6" rx="3"></rect>` : ''}
    </svg>`;
}

const STATUS_CHIP = { DRAFT: 'Draft', DISABLED: 'Paused' };

function rowHTML(m, max, showOwner) {
  const chip = STATUS_CHIP[m.status] ? `<span class="anl-chip">${esc(STATUS_CHIP[m.status])}</span>` : '';
  const owner = showOwner && m.owner_name ? `<span class="anl-sub">${esc(m.owner_name)}</span>` : '';
  const ab = m.ab_tests && (m.ab_tests.running || m.ab_tests.completed)
    ? `${fmtNum(m.ab_tests.running)} / ${fmtNum(m.ab_tests.completed)}`
    : '—';
  return `<tr data-guid="${esc(m.magnet_guid)}" data-owner-guid="${esc(m.owner_guid || '')}">
    <td><a class="anl-name" href="#${magnetHomePath(m.magnet_guid, 'results')}">${esc(m.name)}</a>${chip}${owner}</td>
    <td>${shareBar(Number(m.leads?.window || 0), max)}</td>
    <td><a href="#/leads" data-magnet-leads title="View this magnet’s lead records">${fmtNum(m.leads?.window)}</a></td>
    <td>${fmtNum(m.opens)}</td>
    <td>${fmtRate(m.engagement_rate)}</td>
    <td>${fmtRate(m.completion_rate)}</td>
    <td>${fmtRate(m.lead_rate)}</td>
    <td>${ab}</td>
  </tr>`;
}

/** Exported for tests: the table body, given the API's by_magnet array. */
export function tableHTML(rows, showOwner) {
  const max = rows.reduce((a, r) => Math.max(a, Number(r.leads?.window || 0)), 0);
  return `<div class="anl-wrap"><table class="anl-table">
    <thead><tr>
      <th>Magnet</th><th></th><th>Leads</th><th>Opens</th>
      <th>Engaged</th><th>Completed</th><th>Leads / Open</th><th>A/B run / done</th>
    </tr></thead>
    <tbody>${rows.map((m) => rowHTML(m, max, showOwner)).join('')}</tbody>
  </table></div>`;
}

export function render() {
  periodDays = takePerformancePeriod('/analytics', periodDays);
  // Portfolio mode gets the picker, same as ad-performance and for the same reason: the
  // KPI tiles on this page are cross-client totals, and the house rule is that portfolio
  // mode never shows blended operational data. (A cross-client magnet LEAGUE TABLE would
  // arguably be the more useful reseller view, but that is a product decision, not one to
  // make silently inside a route file.)
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'analytics', title: 'Analytics',
      description: 'Pick a client to see how their magnets are performing.',
    });
  }
  ensureStyles();
  ensureChartStyles();
  return `
    <div class="page-shell" data-section="analytics">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Magnet performance</h1>
          <p class="page-description">How each magnet is performing, so you can tell them apart.</p>
        </div>
        <div class="chart-controls" id="anl-periods">
          ${PERIODS.map((p) => `<button class="chart-period-btn${p.d === periodDays ? ' active' : ''}" type="button" data-days="${p.d}">${p.label}</button>`).join('')}
        </div>
      </div>

      <div class="stats-grid">
        ${statCard({ loading: true, icon: 'person_add', label: 'Leads', valueId: 'anl-leads', href: '#/leads' })}
        ${statCard({ loading: true, icon: 'visibility', label: 'Opens', valueId: 'anl-opens' })}
        ${statCard({ loading: true, icon: 'task_alt', label: 'Completion Rate', valueId: 'anl-completion', href: '#/conversations' })}
        ${statCard({ loading: true, icon: 'widgets', label: 'Active Magnets', valueId: 'anl-active', href: '#/magnets' })}
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">show_chart</span> Opens &amp; Leads</h2>
        </div>
        <div class="card-body" id="anl-trend">
          ${loadingState('chart', 'Loading activity')}
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">leaderboard</span> By magnet</h2>
          <span class="live-indicator" id="anl-range"></span>
        </div>
        <div class="card-body" id="anl-body">
          ${loadingState('list', 'Loading magnet performance')}
        </div>
      </div>
      <p class="anl-note">Leads / Open is the share of visitors who opened a magnet and left contact details. Rates show “—” where nothing opened in the period, rather than 0%.</p>
    </div>`;
}

/**
 * Opens and leads over time — the same chart the dashboard draws, from the same
 * `by_day` series this page ALREADY fetches (it previously used it only to sum a total).
 *
 * WHY IT IS HERE. Analytics was the one page of the three that never adopted
 * services/chart-kit.js: it drew 72x8 sparkline bars inside the table and nothing else,
 * so it had no hover, no keyboard operation, no crosshair and no drill-through, while
 * Dashboard and Ad Performance had all four. Two pages of the same product behaved
 * differently for the same gesture.
 *
 * Built through the kit rather than hand-rolled, for the reason the kit exists: colours
 * come from the --chart-* tokens (so it follows dark mode) and every point is focusable
 * and arrow-key navigable without any per-page work.
 */
function trendDateLabel(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(iso || ''); }
}

function svgTrendChart(rows) {
  return dualAxisChartHTML({
    rows,
    xLabel: (r) => String(r.date || '').slice(5),
    left: { value: (r) => r.opens || 0, label: 'Magnet Opens' },
    right: { value: (r) => r.leads || 0, label: 'Leads' },
    ariaLabel: 'Magnet opens versus leads over time',
    // A day with no leads drills into a guaranteed-empty list, so it is not activatable —
    // the count on the tooltip is already the answer. Same rule as the dashboard.
    activatable: (r) => Number(r.leads || 0) > 0,
    pointLabel: (r) => `${trendDateLabel(r.date)}: ${fmtNum(r.opens || 0)} opens, ${fmtNum(r.leads || 0)} leads`,
    hitData: (r) => ({ day: r.date, leads: Number(r.leads || 0) }),
  });
}

function wireTrend(days) {
  const host = document.getElementById('anl-trend');
  if (!host) return () => {};
  return wireChartHover(host, {
    rows: days,
    tipHTML: (d) => `
      <div class="m5c-tip__title">${trendDateLabel(d.date)}</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--1"></i>${fmtNum(d.opens || 0)} opens</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--2"></i>${fmtNum(d.leads || 0)} leads</div>
      ${Number(d.leads || 0) > 0 ? '<div class="m5c-tip__go">Open these leads →</div>' : ''}`,
    // Lands on /leads already filtered to that day, through the same one-shot hand-off
    // the dashboard uses — not a second filtering path.
    onActivate: (d) => {
      setHandoff(HANDOFF.LEADS_DAY, d.date);
      analyticsLeads({ day: d.date });
      window.location.hash = '#/leads';
    },
  });
}

export async function init() {
  drillScope = leadsHandoffScope(); drillRange = {};
  if (inPortfolio()) {
    return initPicker(apiBase(), [
      { h: 'Leads', td: (r) => fmtNum(r.leads.window ?? r.leads.total) },
      { h: 'Opens', td: (r) => fmtNum(r.opens) },
      { h: 'Completed', td: (r) => fmtRate(r.completion_rate) },
    ]);
  }
  mountReportingSurface('magnets');
  ensureStyles();
  ensureChartStyles();   // the kit owns the --chart-* backed mark styles
  document.getElementById('anl-body')?.addEventListener('click', event => {
    const row = event.target.closest('tr[data-guid]');
    if (!row || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const context = { ...drillRange, magnetGuid: row.dataset.guid, ownerGuid: row.dataset.ownerGuid };
    if (event.target.closest('[data-magnet-leads]')) analyticsLeads(context);
    else if (event.target.closest('.anl-name')) setMagnetResultsHandoff({ ...drillScope, ...context, returnTo: { hash: '#/analytics', label: 'Back to Magnet comparison' } });
  });
  document.getElementById('anl-leads')?.closest('a')?.addEventListener('click', event => {
    if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) analyticsLeads(drillRange);
  });
  await load();

  const periods = document.getElementById('anl-periods');
  if (periods) {
    periods.addEventListener('click', (e) => {
      const btn = e.target.closest('.chart-period-btn');
      if (!btn) return;
      periodDays = Number(btn.dataset.days) || 30;
      periods.querySelectorAll('.chart-period-btn').forEach((b) => {
        b.classList.toggle('active', Number(b.dataset.days) === periodDays);
      });
      load();
    });
  }
}

async function load() {
  const loadingTarget = document.getElementById('anl-body');
  const finishRefresh = beginRefresh(loadingTarget, 'Updating magnet performance');
  const body = document.getElementById('anl-body');
  const range = document.getElementById('anl-range');
  if (!body) return;


  const now = new Date();
  const to = ymd(now);
  const from = ymd(new Date(now.getTime() - (periodDays - 1) * 86400000));
  if (range) range.textContent = `${from} → ${to}`;
  drillRange = { from, to };

  let data;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/dashboard/stats?from=${from}&to=${to}&group_by=magnet`);
    data = await res.json();
    failIfNotOk(res, data);
    if (!data?.ok) throw new Error(data?.error || 'Failed to load');
    drillRange = { from: data.from || from, to: data.to || to };
    if (range) range.textContent = `${drillRange.from} → ${drillRange.to}`;
  } catch (err) {
    failLoadingStates(loadingTarget?.closest('.page-shell'));
    // Say the request failed. Rendering an empty table here would be indistinguishable
    // from "you have no magnets", which is a very different thing to tell someone.
    body.innerHTML = isDenial(err) ? denialState() : emptyState('cloud_off', 'Could not load analytics. Refresh to try again.');
    console.error('[analytics] load failed:', err);
    return;
  } finally {
    finishRefresh();
    settleLoadingValues(loadingTarget?.closest('.page-shell'));
  }

  const setText = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  setText('anl-leads', fmtNum(data.by_day?.reduce((a, d) => a + Number(d.leads || 0), 0)));
  setText('anl-opens', fmtNum(data.funnel?.opens));
  setText('anl-completion', fmtRate(data.funnel?.completion_rate));
  setText('anl-active', fmtNum(data.magnets?.active));

  // Trend chart. Rendered BEFORE the by-magnet early-returns below: a workspace with no
  // magnets still has a period selected, and an empty chart card that explains itself is
  // better than a card stuck on "Loading…" forever because an unrelated guard returned.
  const days = Array.isArray(data.by_day) ? data.by_day : [];
  const trendHost = document.getElementById('anl-trend');
  if (trendHost) {
    const anyTrend = days.some((d) => Number(d.opens || 0) > 0 || Number(d.leads || 0) > 0);
    trendHost.innerHTML = anyTrend ? svgTrendChart(days)
      : emptyState('show_chart', 'No activity in this range yet. Opens and leads chart here as visitors engage your magnets.');
    if (anyTrend) wireTrend(days);
  }

  const rows = Array.isArray(data.by_magnet) ? data.by_magnet : [];
  if (!rows.length) {
    body.innerHTML = emptyState('widgets', 'No magnets yet. Create one and its numbers will show up here.');
    return;
  }

  // Every magnet exists but none has been opened in this window. Distinct from having no
  // magnets, and the fix is different — one is "build something", the other is "nobody
  // has seen what you built".
  const anyActivity = rows.some((m) => Number(m.opens || 0) > 0 || Number(m.leads?.window || 0) > 0);

  // The owner column only earns its width when there is more than one owner in the data.
  const showOwner = new Set(rows.map((m) => m.owner_guid)).size > 1;

  const truncated = data.by_magnet_truncated
    ? `<p class="anl-warn">Showing the top ${rows.length} magnets by leads. You have more than that, so this list is not complete.</p>`
    : '';
  const quiet = !anyActivity
    ? `<p class="anl-warn">No opens or leads for any magnet in this period. Try a longer range.</p>`
    : '';

  body.innerHTML = truncated + quiet + tableHTML(rows, showOwner);
  const anlBody = body.querySelector('.anl-table tbody');
  if (anlBody) capList(anlBody, { rowSelector: 'tr', noun: 'magnets', controlHost: body.querySelector('.anl-wrap') || body });
}
